import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { XMarkIcon, ArrowLeftIcon, BeakerIcon, BoltIcon, BriefcaseIcon, MagnifyingGlassIcon, LockClosedIcon, WrenchScrewdriverIcon } from '@heroicons/react/24/outline';
import CheckoutModal from './CheckoutModal';
import Modal from './Modal';
import CheckoutPage from '../../features/pricing/CheckoutPage';

const PRODUCTS = {
  operations: {
    name: 'Operations Package',
    description: 'Full access to operations optimization features',
    price: 49,
    priceId: 'price_1Qod0mJ0cEYz9u0GBcXjkW8t' // Ops Sentinel
  },
  specialized: {
    name: 'Specialized Package',
    description: 'Advanced features for specialized industries',
    price: 99,
    priceId: 'price_1Qod0mJ0cEYz9u0GBcXjkW8t' // Ops Sentinel
  }
};

const PaymentModal = ({ isOpen, onClose, userSubscriptions = [], view = 'operations', refreshUser }) => {
  const [selectedAgent, setSelectedAgent] = useState('operations');
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  
  if (!isOpen) return null;

  const hasBasePlatform = userSubscriptions.includes('base');

  const basePlatform = {
    name: 'Growth Catalyst Platform',
    price: 79,
    priceId: 'price_1QoczGJ0cEYz9u0GxvbeQNm8', // Growth Catalyst
    description: 'Your AI-powered COO that works 24/7. Get the strategic advantage of an executive team at a fraction of the cost.',
    primaryFeatures: [
      'Continuous business process learning & memory',
      'Daily executive briefings',
      '24/7 anomaly detection',
      'Basic workflow automation (50 actions/day)',
      '10GB encrypted knowledge storage'
    ],
    benefits: [
      'Replace $15k/mo consultancy retainers',
      'Works 168 hours/week',
      'Learns your business inside-out',
      'Immediate ROI tracking',
      'Risk mitigation'
    ]
  };

  const specializedModules = {
    revenue: {
      name: 'Revenue Radar',
      price: 99,
      priceId: 'price_1QoczsJ0cEYz9u0GBQQIkFAW',  // Revenue Radar
      description: 'AI-powered revenue optimization and forecasting system that identifies opportunities and prevents churn.',
      features: [
        'Real-time pricing optimization',
        'Advanced lead scoring',
        'Churn prediction & prevention',
        'Revenue opportunity detection',
        'Customer lifecycle optimization'
      ],
      roi: 'Optimize pricing and reduce customer churn'
    },
    market: {
      name: 'Market Alchemist',
      price: 149,
      priceId: 'price_1Qod0PJ0cEYz9u0GUeIB5RGc',  // Market Alchemist
      description: 'Transform market data into actionable strategies. Stay ahead of trends and competition.',
      features: [
        'Social listening & trend analysis',
        'Competitor movement tracking',
        'Campaign performance prediction',
        'Market opportunity mapping',
        'Brand sentiment analysis'
      ],
      roi: 'Make data-driven marketing decisions'
    },
    ops: {
      name: 'Ops Sentinel',
      price: 129,
      priceId: 'price_1Qod0mJ0cEYz9u0GBcXjkW8t',  // Ops Sentinel
      description: 'Optimize operations with AI-powered logistics and compliance monitoring.',
      features: [
        'Inventory optimization AI',
        'Smart logistics routing',
        'Compliance monitoring',
        'Process efficiency analysis',
        'Supply chain optimization'
      ],
      roi: 'Streamline operations and reduce inefficiencies'
    }
  };

  const platformFeatures = {
    learning: {
      name: 'Continuous Learning & Adaptation',
      icon: BeakerIcon,
      description: 'Midus becomes an extension of your business intelligence, learning and adapting 24/7.',
      details: [
        'Learns your business processes, preferences, and goals',
        'Builds comprehensive knowledge base of your operations',
        'Identifies patterns and optimization opportunities',
        'Adapts recommendations based on your feedback',
        'Maintains institutional memory as your business grows'
      ],
      examples: [
        'Automatically documents and optimizes your SOPs',
        'Learns from every interaction to provide better suggestions',
        'Builds context-aware responses based on your business history'
      ]
    },
    briefings: {
      name: 'Proactive Business Intelligence',
      icon: BriefcaseIcon,
      description: 'Start each day with actionable insights and end it with comprehensive progress tracking.',
      details: [
        'Daily morning briefings with prioritized actions',
        'Real-time market and competitor monitoring',
        'Industry trend analysis and opportunity alerts',
        'End-of-day performance summaries',
        'Weekly strategy recommendations'
      ],
      examples: [
        'Morning brief: "3 key competitor moves from overnight"',
        'Afternoon alert: "Trending topic relevant to your industry"',
        `Evening summary: "Today's wins and tomorrow's priorities"`
      ]
    },
    automation: {
      name: 'Intelligent Workflow Automation',
      icon: BoltIcon,
      description: 'Your 24/7 operations team that works while you sleep.',
      details: [
        'Automated task prioritization and assignment',
        'Smart process optimization',
        'Continuous workflow monitoring',
        'Anomaly detection and early warning system',
        'Automated reporting and analytics'
      ],
      examples: [
        'Automatically reorganizes daily priorities based on new developments',
        'Monitors industry news and alerts you of relevant changes',
        'Suggests process improvements based on performance data'
      ]
    },
    research: {
      name: 'Continuous Research & Strategy',
      icon: MagnifyingGlassIcon,
      description: 'Never miss an opportunity with round-the-clock market research and strategic planning.',
      details: [
        'Continuous market research and analysis',
        'Competitor monitoring and benchmarking',
        'Industry trend identification',
        'Strategic opportunity detection',
        'Risk assessment and mitigation planning'
      ],
      examples: [
        'Overnight analysis of new market opportunities',
        'Real-time competitive advantage identification',
        'Proactive risk mitigation recommendations'
      ]
    },
    knowledge: {
      name: 'Secure Knowledge Management',
      icon: LockClosedIcon,
      description: 'Your business intelligence, securely stored and instantly accessible.',
      details: [
        '10GB encrypted knowledge storage',
        'Automatic information organization',
        'Intelligent search and retrieval',
        'Context-aware knowledge base',
        'Secure document processing and analysis'
      ],
      examples: [
        'Instantly access any business decision history',
        'Auto-organized documentation and insights',
        'Secure storage of sensitive business data'
      ]
    }
  };

  const product = PRODUCTS[view];

  if (!product) return null;

  // Feature deep-dive view
  if (selectedFeature) {
    const feature = platformFeatures[selectedFeature];
    if (!feature) {
      if (selectedFeature === 'payment') {
        return (
          <div className="fixed inset-0 flex items-center justify-center z-50">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setSelectedFeature(null)}></div>
            <div className="relative bg-[#2a2a2a]/90 backdrop-blur-sm rounded-lg shadow-lg border border-white/[0.08] w-[900px] h-[85vh] flex flex-col">
              <div className="flex-1 overflow-y-auto">
                <div className="p-8">
                  <button 
                    onClick={() => setSelectedFeature(null)}
                    className="absolute top-6 right-6 text-gray-400 hover:text-black"
                  >
                    <XMarkIcon className="w-6 h-6" />
                  </button>
                  
                  <div className="max-w-2xl mx-auto">
                    <h2 className="text-2xl font-medium text-gray-200 mb-6">Complete Your Subscription</h2>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      }
      setSelectedFeature(null);
      return null;
    }
    
    return (
      <div className="fixed inset-0 flex items-center justify-center z-50">
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setSelectedFeature(null)}></div>
        <div className="relative bg-[#2a2a2a]/90 backdrop-blur-sm rounded-lg shadow-lg border border-white/[0.08] w-[900px] h-[85vh] flex flex-col">
          <div className="flex-1 overflow-y-auto">
            <div className="p-8">
              <button 
                onClick={() => setSelectedFeature(null)}
                className="absolute top-6 right-6 text-gray-400 hover:text-black"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
              
              <div className="flex items-center gap-4 mb-6">
                <feature.icon className="w-10 h-10 text-gray-200" />
                <div>
                  <h2 className="text-2xl font-medium text-gray-200">{feature.name}</h2>
                  <p className="text-gray-400 mt-1">{feature.description}</p>
                </div>
              </div>

              <div className="space-y-8">
                <div>
                  <h3 className="text-lg font-medium text-gray-200 mb-4">Key Capabilities</h3>
                  <ul className="space-y-3">
                    {feature.details.map((detail, index) => (
                      <li key={index} className="flex items-start">
                        <span className="text-blue-400 mr-3">•</span>
                        <span className="text-gray-300">{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-200 mb-4">Real World Examples</h3>
                  <ul className="space-y-3">
                    {feature.examples.map((example, index) => (
                      <li key={index} className="flex items-start">
                        <span className="text-green-400 mr-3">→</span>
                        <span className="text-gray-300">{example}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-lg p-6 border border-blue-500/20">
                  <h3 className="text-lg font-medium text-gray-200 mb-3">Why This Matters</h3>
                  <p className="text-gray-300">
                    Traditional businesses spend countless hours and resources managing these tasks manually. 
                    Midus handles this automatically, 24/7, allowing you to focus on strategic decisions 
                    and growth opportunities. This single feature often replaces multiple tool subscriptions 
                    and part-time staff.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-between items-center px-8 py-6 bg-[#1e1e1e]/80 border-t border-white/[0.08]">
            <div className="text-gray-400">
              <span className="text-gray-200 font-medium">Part of the Growth Catalyst Platform</span>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setSelectedFeature(null)}
                className="px-4 py-2 rounded-md text-gray-200 hover:bg-[#EDEDED]/80 transition-colors"
              >
                Back
              </button>
              <button
                onClick={() => {
                  navigate(`/pricing/${view}/checkout`, {
                    state: { 
                      from: location.pathname,
                      priceId: basePlatform.priceId,
                      addons: selectedAddons,
                      refreshUser: refreshUser
                    }
                  });
                }}
                className="px-6 py-2 bg-blue-500 text-black rounded-md hover:bg-blue-600 transition-colors"
              >
                Subscribe Now
              </button>
            </div>
          </div>

          <div className="mt-8 bg-gradient-to-r from-amber-500/10 via-purple-500/10 to-blue-500/10 rounded-lg p-6 border border-amber-500/20">
            <div className="flex items-start gap-4">
              <div className="text-3xl">🛠️</div>
              <div>
                <h3 className="text-xl font-medium text-gray-200 mb-2">Custom Solutions</h3>
                <p className="text-gray-300">
                  Every business is unique. Work with our team to create a custom AI agent specifically tailored to your industry 
                  and business needs. Your custom agent will seamlessly integrate with your existing Midus suite, creating 
                  a truly personalized AI workforce.
                </p>
                <button 
                  onClick={() => window.location.href = 'mailto:hello@heymidus.com'}
                  className="mt-4 text-amber-400 hover:text-amber-300 transition-colors text-sm flex items-center gap-2"
                >
                  Contact us about custom solutions
                  <span>→</span>
                </button> 
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show base platform view if user hasn't subscribed
  if (!hasBasePlatform) {
    return (
      <div className="fixed inset-0 flex items-center justify-center z-50">
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose}></div>
        <div className="relative bg-[#2a2a2a]/90 backdrop-blur-sm rounded-lg shadow-lg border border-white/[0.08] w-[900px] h-[85vh] flex flex-col">
          <div className="flex-1 overflow-y-auto">
            <div className="p-8">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="text-3xl font-medium text-gray-200">Growth Catalyst Platform</h2>
                  <p className="text-xl text-gray-400 mt-2">Your AI-powered COO that never sleeps</p>
                </div>
                <div className="flex items-baseline">
                  <span className="text-4xl font-bold text-gray-200">${basePlatform.price}</span>
                  <span className="text-gray-400 ml-2">/month</span>
                </div>
              </div>

              <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-lg p-6 border border-blue-500/20 mb-8">
                <h3 className="text-xl font-medium text-gray-200 mb-3">Why Growth Catalyst?</h3>
                <p className="text-gray-300">
                  Get the power of an entire executive team at 1/10th the cost. Midus learns your business,
                  identifies opportunities, and helps you scale faster than ever before.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-8 mb-8">
                <div>
                  <h3 className="text-lg font-medium text-gray-200 mb-4">Platform Features</h3>
                  <ul className="space-y-3">
                    {Object.entries(platformFeatures).map(([key, feature]) => (
                      <li 
                        key={key}
                        onClick={() => setSelectedFeature(key)}
                        className="flex items-start p-3 rounded-lg cursor-pointer hover:bg-white/[0.02] transition-colors"
                      >
                        <feature.icon className="w-6 h-6 mr-3" />
                        <div>
                          <div className="text-gray-200 font-medium">{feature.name}</div>
                          <div className="text-sm text-gray-400">{feature.description}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-200 mb-4">Business Impact</h3>
                  <ul className="space-y-3">
                    {basePlatform.benefits.map((benefit, index) => (
                      <li key={index} className="flex items-start">
                        <span className="text-green-400 mr-3">↗</span>
                        <span className="text-gray-300">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="space-y-8">
                <div className="bg-gradient-to-r from-purple-500/10 to-blue-500/10 rounded-lg p-6 border border-purple-500/20">
                  <h3 className="text-xl font-medium text-gray-200 mb-3">Unlock Specialized Modules</h3>
                  <p className="text-gray-300">
                    Supercharge your growth with specialized AI modules for Revenue, Marketing, and Operations.
                    Each module seamlessly integrates with the base platform for exponential results.
                  </p>
                  <div className="grid grid-cols-3 gap-4 mt-4">
                    {Object.entries(specializedModules).map(([key, module]) => (
                      <div 
                        key={key}
                        className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                          selectedAddons.includes(module.priceId)
                            ? 'border-blue-500 bg-blue-500/10'
                            : 'border-white/[0.08] hover:border-white/20'
                        }`}
                        onClick={() => toggleAddon(key)}
                      >
                        <div className="flex justify-between items-center mb-2">
                          <h4 className="text-gray-200 font-medium">{module.name}</h4>
                          <span className="text-gray-400">+${module.price}/mo</span>
                        </div>
                        <p className="text-xs text-gray-400">{module.roi}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-gradient-to-r from-amber-500/10 via-purple-500/10 to-blue-500/10 rounded-lg p-6 border border-amber-500/20">
                  <div className="flex items-start gap-4">
                    <div className="text-gray-200">
                      <WrenchScrewdriverIcon className="w-8 h-8" />
                    </div>
                    <div>
                      <h3 className="text-xl font-medium text-gray-200 mb-2">Need Something More Specific?</h3>
                      <p className="text-gray-300">
                        After getting started with the Growth Catalyst Platform, work with our team to create custom AI agents 
                        tailored to your unique industry and business needs. Your custom agents will seamlessly integrate with 
                        your existing Midus suite, creating a truly personalized AI workforce.
                      </p>
                      <button 
                        onClick={() => window.location.href = 'mailto:hello@heymidus.com'}
                        className="mt-4 text-amber-400 hover:text-amber-300 transition-colors text-sm flex items-center gap-2"
                      >
                        Learn more about custom solutions
                        <span>→</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-between items-center px-8 py-6 bg-[#1e1e1e]/80 border-t border-white/[0.08]">
            <div className="text-gray-400">
              <span className="text-gray-200 font-medium">Unlock full platform capabilities</span>
            </div>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-md text-gray-200 hover:bg-[#EDEDED]/80 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  navigate(`/pricing/${view}/checkout`, {
                    state: { 
                      from: location.pathname,
                      priceId: basePlatform.priceId,
                      addons: selectedAddons,
                      refreshUser: refreshUser
                    }
                  });
                }}
                className="px-6 py-2 bg-blue-500 text-black rounded-md hover:bg-blue-600 transition-colors"
              >
                Subscribe Now
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const toggleAddon = (addonId) => {
    const addon = specializedModules[addonId];
    if (!addon) {
      console.error('Invalid addon:', addonId);
      return;
    }
    
    setSelectedAddons(prev => {
      const newAddons = prev.includes(addon.priceId)
        ? prev.filter(id => id !== addon.priceId)
        : [...prev, addon.priceId];
      console.log('New addons:', newAddons);
      return newAddons;
    });
  };

  const handleSubscribe = () => {
    console.log('Subscribing with addons:', selectedAddons);
    navigate(`/pricing/${view}/checkout`, {
      state: { 
        from: location.pathname,
        priceId: basePlatform.priceId,
        addons: selectedAddons,
        refreshUser: refreshUser
      }
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Subscribe to ${product.name}`}
    >
      <div className="p-6">
        <div className="mb-6">
          <h3 className="text-xl font-semibold mb-2">{product.name}</h3>
          <p className="text-gray-400">{product.description}</p>
          <p className="text-2xl font-bold mt-2">${product.price}/month</p>
        </div>

        {error && (
          <div className="bg-red-500 bg-opacity-10 border border-red-500 rounded p-3 mb-4">
            <p className="text-red-500 text-sm">{error}</p>
          </div>
        )}

        <button
          onClick={handleSubscribe}
          className="px-6 py-2 bg-blue-500 text-black rounded-md hover:bg-blue-600 transition-colors"
        >
          Subscribe Now
        </button>
      </div>
    </Modal>
  );
};

export default PaymentModal; 